import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useFocusable } from '@/hooks/useFocusable';
import { FocusContext, setFocus, getCurrentFocusKey } from '@noriginmedia/norigin-spatial-navigation';
import { useTranslation } from 'react-i18next';
import { MarqueeText } from '@/components/common/MarqueeText';
import throttle from '@/utils/throttle';
import {
  ProfileIcon,
  HistoryIcon,
  LogoutIcon,
  HelpIcon,
  AboutIcon,
  SettingsIcon,
  VipBadgeIcon,
  GoogleIcon,
  AppleIcon,
  FacebookIcon,
  LineIcon,
  WeChatIcon,
  PhoneIcon,
  EmailIcon,
} from '@/components/svg';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { VipInfo } from '@/hooks/useVipInfo';
import { getAccountType, AccountType } from '@/utils/accountUtils';
import { filterEmoji } from '@/utils/filterEmoji';
import qqIconImage from '@/assets/images/account/login_type_qq.png';
import styles from './ProfileMenu.module.scss';

interface ProfileMenuProps {
  focusKey: string;
  isLoggedIn?: boolean;
  userName?: string;
  userAvatar?: string;
  vipInfo?: VipInfo | null;
  onMenuSelect?: (menuKey: string) => void;
  onFocusChange?: () => void;
}

interface MenuItem {
  key: string;
  labelKey: string; // i18n key
  IconComponent: React.FC<{ className?: string }>;
  reportData?: string;
}

// 未登录菜单项
const getLoggedOutMenuItems = (): MenuItem[] => [
  {
    key: 'login',
    labelKey: 'profile.login',
    IconComponent: ProfileIcon,
    reportData: `mod_id=navbar&button_name=login`,
  },
  {
    key: 'history',
    labelKey: 'profile.history',
    IconComponent: HistoryIcon,
    reportData: `mod_id=navbar&button_name=history`,
  },
  {
    key: 'feedback',
    labelKey: 'profile.helpFeedback',
    IconComponent: HelpIcon,
    reportData: `mod_id=navbar&button_name=help`,
  },
  {
    key: 'about',
    labelKey: 'profile.about',
    IconComponent: AboutIcon,
    reportData: `mod_id=navbar&button_name=about`,
  },
  {
    key: 'settings',
    labelKey: 'profile.settings',
    IconComponent: SettingsIcon,
    reportData: `mod_id=navbar&button_name=settings`,
  },
];

// 已登录菜单项
const getLoggedInMenuItems = (): MenuItem[] => [
  {
    key: 'history',
    labelKey: 'profile.history',
    IconComponent: HistoryIcon,
    reportData: `mod_id=navbar&button_name=history`,
  },
  {
    key: 'feedback',
    labelKey: 'profile.helpFeedback',
    IconComponent: HelpIcon,
    reportData: `mod_id=navbar&button_name=help`,
  },
  {
    key: 'about',
    labelKey: 'profile.about',
    IconComponent: AboutIcon,
    reportData: `mod_id=navbar&button_name=about`,
  },
  {
    key: 'settings',
    labelKey: 'profile.settings',
    IconComponent: SettingsIcon,
    reportData: `mod_id=navbar&button_name=settings`,
  },
  {
    key: 'logout',
    labelKey: 'profile.logout',
    IconComponent: LogoutIcon,
    reportData: `mod_id=navbar&button_name=logout`,
  },
];

// 菜单项组件 - 提取到外部避免重复创建
const MenuItemComponent: React.FC<{
  item: MenuItem;
  index: number;
  totalItems: number;
  focusKey: string;
  onMenuClick: (menuKey: string) => void;
  onCloseMenu: () => void;
  onItemReady?: (index: number) => void;
  firstItemRef?: React.RefObject<HTMLButtonElement>;
  setFocusDelayed: (focusKey: string, delay?: number) => void;
  t: (key: string, options?: Record<string, any>) => string;
}> = React.memo(
  ({
    item,
    index,
    totalItems,
    focusKey,
    onMenuClick,
    onCloseMenu,
    onItemReady,
    firstItemRef,
    setFocusDelayed,
    t,
    ...rest
  }) => {
    const itemFocusKey = `${focusKey}-menu-item-${index}`;

    const { ref, focused } = useFocusable({
      focusKey: itemFocusKey,
      onEnterPress: () => onMenuClick(item.key),
      onArrowPress: (direction) => {
        // 上下方向：循环导航
        if (direction === 'down') {
          const nextIndex = (index + 1) % totalItems;
          setFocusDelayed(`${focusKey}-menu-item-${nextIndex}`);
          return false;
        }
        if (direction === 'up') {
          const prevIndex = (index - 1 + totalItems) % totalItems;
          setFocusDelayed(`${focusKey}-menu-item-${prevIndex}`);
          return false;
        }
        // 左方向：阻止导航（菜单是最左侧）
        if (direction === 'left') {
          return false;
        }
        // 右方向：关闭菜单并导航到VIP按钮
        if (direction === 'right') {
          onCloseMenu();
          setFocusDelayed('header-join-vip');
          return false; // 阻止默认导航，我们手动处理
        }
        return true;
      },
      onBackPress: () => {
        // 菜单项按返回键：不处理，让全局处理器显示退出弹窗
        // 返回 false 表示未处理，允许传播到全局处理器
        return false;
      },
      onFocus: () => {
        // 菜单项聚焦回调
      },
    });

    // 通知父组件该菜单项已准备好
    useEffect(() => {
      if (index === 0) {
        // 只有第一个菜单项通知准备就绪
        onItemReady?.(index);
      }
    }, [index, onItemReady, itemFocusKey]);

    const { IconComponent } = item;

    // 简化 ref 处理，直接使用 useFocusable 的 ref，单独处理 firstItemRef
    useEffect(() => {
      if (index === 0 && firstItemRef && ref && 'current' in ref && ref.current) {
        // eslint-disable-next-line no-param-reassign
        (firstItemRef as React.MutableRefObject<HTMLButtonElement | null>).current = ref.current;
      }
    }, [index, firstItemRef, ref]);

    return (
      <button
        ref={ref}
        className={`${styles.menuItem} ${focused ? styles.menuItemFocused : ''}`}
        onClick={() => onMenuClick(item.key)}
        onMouseEnter={() => setFocus(itemFocusKey)}
        {...rest}
      >
        <span className={`${styles.menuIcon} ${focused ? styles.menuIconFocused : ''}`}>
          <IconComponent className={styles.menuIconSvg} />
        </span>
        {/* 使用 MarqueeText 组件替代原来的简单文本 */}
        <MarqueeText
          text={t(item.labelKey)}
          focused={focused}
          className={styles.menuLabel}
          delay={800}
          speed={60}
          gap={80}
        />
      </button>
    );
  }
);

// 用户信息展示组件（不可获焦）
interface UserInfoDisplayProps {
  userAvatar: string;
  userName: string;
  isVip: boolean;
  vipEndDate: string;
  vipEndTimestamp: number;
  accountType: AccountType | null;
  tips?: string; // 添加tips字段
  t: (key: string, options?: Record<string, any>) => string;
}

const UserInfoDisplay: React.FC<UserInfoDisplayProps> = React.memo(
  ({ userAvatar, userName, isVip, vipEndDate, vipEndTimestamp, accountType, tips, t }) => {
    // 将秒级时间戳转换为毫秒级时间戳
    const vipEndTimestampMs = vipEndTimestamp * 1000;

    // 判断会员是否过期：曾经是会员（有到期时间）但已过期
    const isVipExpired = vipEndTimestamp > 0 && vipEndTimestampMs < Date.now() && !isVip;

    // 计算过期天数
    const expiredDays = isVipExpired ? Math.floor((Date.now() - vipEndTimestampMs) / (1000 * 60 * 60 * 24)) : 0;

    // 过滤用户昵称中的emoji和特殊字符
    const filteredUserName = filterEmoji(userName || 'User');

    // 根据账号类型渲染对应图标
    const renderAccountIcon = () => {
      if (!accountType) return null;

      const iconProps = { className: styles.accountTypeIcon, size: 20 };

      switch (accountType) {
        case AccountType.Google:
          return <GoogleIcon {...iconProps} />;
        case AccountType.Apple:
          return <AppleIcon {...iconProps} />;
        case AccountType.Facebook:
          return <FacebookIcon {...iconProps} />;
        case AccountType.Line:
          return <LineIcon {...iconProps} />;
        case AccountType.WeChat:
          return <WeChatIcon {...iconProps} />;
        case AccountType.QQ:
          return (
            <img src={qqIconImage} alt="QQ" className={styles.accountTypeIcon} style={{ width: 20, height: 20 }} />
          );
        case AccountType.Phone:
          return <PhoneIcon {...iconProps} />;
        case AccountType.Email:
          return <EmailIcon {...iconProps} />;
        default:
          return null;
      }
    };

    return (
      <div className={styles.userInfoSection}>
        {/* 头像区域 */}
        <div className={styles.userAvatarWrapper}>
          {userAvatar ? (
            <img src={userAvatar} alt={filteredUserName} className={styles.userAvatarImage} />
          ) : (
            <ProfileIcon className={styles.userAvatarPlaceholder} />
          )}
        </div>

        {/* 用户信息区域 */}
        <div className={styles.userInfoContent}>
          {/* 第一行：昵称 + 账号类型图标 + VIP 标签 */}
          <div className={styles.userInfoRow}>
            <span className={styles.userNickname}>{filteredUserName}</span>
            {renderAccountIcon()}
            {(isVip || isVipExpired) && (
              <span className={`${styles.userVipTag} ${isVipExpired ? styles.userVipTagExpired : ''}`}>VIP</span>
            )}
          </div>
          {/* 第二行：VIP 到期时间（仅会员或过期会员显示）或非会员提示 */}
          {(isVip || isVipExpired) && (
            <div className={styles.userVipExpiry}>
              {isVipExpired
                ? t('profile.vipExpired', { days: expiredDays })
                : `${t('profile.vipExpiring')}${vipEndDate}`}
            </div>
          )}
          {/* 非会员提示：显示后台返回的tips */}
          {!isVip && !isVipExpired && tips && <div className={styles.userTips}>{tips}</div>}
        </div>
      </div>
    );
  }
);

export const ProfileMenu: React.FC<ProfileMenuProps> = ({
  focusKey,
  isLoggedIn = false,
  userName = '',
  userAvatar = '',
  vipInfo,
  onMenuSelect,
  onFocusChange,
  ...props
}) => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [accountType, setAccountType] = useState<AccountType | null>(null);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [menuItemsReady, setMenuItemsReady] = useState(false);
  const firstMenuItemRef = useRef<HTMLButtonElement>(null);
  const menuTimerRef = useRef<number | null>(null); // 用于管理菜单相关定时器

  // 辅助函数：延迟设置焦点
  const setFocusDelayed = useCallback((focusKey: string, delay = 0) => {
    setTimeout(() => setFocus(focusKey), delay);
  }, []);

  // 辅助函数：多重延迟设置焦点（用于确保菜单渲染完成）
  const setFocusWithMultipleRetries = useCallback(
    (focusKey: string) => {
      setFocusDelayed(focusKey, 0);
      setFocusDelayed(focusKey, 100);
      setFocusDelayed(focusKey, 200);
    },
    [setFocusDelayed]
  );

  // 获取账号类型
  useEffect(() => {
    if (isLoggedIn) {
      const type = getAccountType();
      setAccountType(type);
    }
  }, [isLoggedIn]);

  // 组件卸载时清理定时器
  useEffect(() => {
    return () => {
      if (menuTimerRef.current) {
        clearTimeout(menuTimerRef.current);
      }
    };
  }, []);

  const menuFocusKey = `${focusKey}-menu`;

  // 根据登录状态选择菜单项
  const menuItems = isLoggedIn ? getLoggedInMenuItems() : getLoggedOutMenuItems();

  // 监听登录状态变化，重置菜单项准备状态
  useEffect(() => {
    setMenuItemsReady(false);
    // 登录状态变化时，如果菜单打开，重新设置焦点
    if (isMenuOpen && !showLogoutConfirm) {
      setTimeout(() => {
        const targetFocusKey = `${menuFocusKey}-menu-item-0`;
        setFocus(targetFocusKey);
      }, 100);
    }
  }, [isLoggedIn, isMenuOpen, showLogoutConfirm, menuFocusKey]);

  // 监听菜单打开状态，确保焦点正确设置
  useEffect(() => {
    if (isMenuOpen && !showLogoutConfirm) {
      // 重置菜单项准备状态
      setMenuItemsReady(false);

      // 菜单打开时，使用多重策略确保第一个菜单项获得焦点
      const targetFocusKey = `${menuFocusKey}-menu-item-0`;

      // 立即尝试设置焦点
      setFocus(targetFocusKey);

      // 短延迟后再次尝试
      const timer1 = setTimeout(() => {
        setFocus(targetFocusKey);
      }, 50);

      // 较长延迟后最后一次尝试
      const timer2 = setTimeout(() => {
        setFocus(targetFocusKey);
        setMenuItemsReady(true);
      }, 200);

      return () => {
        clearTimeout(timer1);
        clearTimeout(timer2);
      };
    }
  }, [isMenuOpen, showLogoutConfirm, menuFocusKey]);

  // 监听菜单打开状态，确保焦点正确设置
  useEffect(() => {
    if (menuItemsReady && isMenuOpen && !showLogoutConfirm) {
      const targetFocusKey = `${menuFocusKey}-menu-item-0`;
      setFocus(targetFocusKey);
    }
  }, [menuItemsReady, isMenuOpen, showLogoutConfirm, menuFocusKey]);

  // 监听当前焦点，当焦点不在 ProfileMenu 相关元素上时自动关闭菜单
  useEffect(() => {
    // 如果菜单未打开或者正在显示退出确认对话框，不执行焦点检查
    if (!isMenuOpen || showLogoutConfirm) {
      return;
    }

    // 使用 focusin 事件监听焦点变化，替代 setInterval 轮询
    const handleFocusChange = () => {
      const currentFocusKey = getCurrentFocusKey();
      if (!currentFocusKey) return;

      // 检查焦点是否在 ProfileMenu 相关元素上
      const isOnProfileEntry = currentFocusKey === `${focusKey}-entry`;
      const isOnMenuItem = currentFocusKey.startsWith(`${menuFocusKey}-menu-item-`);
      // 检查焦点是否在确认对话框上（双重保险，虽然 showLogoutConfirm 已经会阻止执行）
      const isOnConfirmDialog = currentFocusKey.startsWith('confirm-dialog-');
      const isOnProfileMenu = isOnProfileEntry || isOnMenuItem || isOnConfirmDialog;

      // 如果焦点不在 ProfileMenu 相关元素上，关闭菜单
      if (!isOnProfileMenu) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('focusin', handleFocusChange);

    return () => {
      document.removeEventListener('focusin', handleFocusChange);
    };
  }, [isMenuOpen, showLogoutConfirm, focusKey, menuFocusKey]);

  // 个人中心入口的焦点管理
  const { ref: entryRef, focused: entryFocused } = useFocusable({
    focusKey: `${focusKey}-entry`,
    onEnterPress: () => {
      // 按确认键时，焦点进入菜单第一项
      const targetFocusKey = `${menuFocusKey}-menu-item-0`;
      setFocusWithMultipleRetries(targetFocusKey);
    },
    onFocus: () => {
      // 通知父组件焦点变化
      onFocusChange?.();
      // 焦点到达时自动展开菜单（但不移动焦点）
      setIsMenuOpen(true);
    },
    onBlur: () => {
      // 焦点离开入口时，如果焦点不在菜单内，则关闭菜单
      // 延迟检查，确保新焦点已设置
      if (menuTimerRef.current) {
        clearTimeout(menuTimerRef.current);
      }
      menuTimerRef.current = window.setTimeout(() => {
        const currentFocusKey = getCurrentFocusKey();
        if (!currentFocusKey?.startsWith(menuFocusKey)) {
          setIsMenuOpen(false);
        }
      }, 50);
    },
    onBackPress: () => {
      // 个人中心入口按返回键：不处理，让全局处理器显示退出弹窗
      // 返回 false 表示未处理，允许传播到全局处理器
      return false;
    },
    onArrowPress: (direction) => {
      // 阻止往左导航（ProfileMenu 是最左侧元素）
      if (direction === 'left') {
        return false;
      }
      // 右向导航：如果菜单打开，关闭菜单并导航到VIP按钮
      if (direction === 'right') {
        if (isMenuOpen) {
          setIsMenuOpen(false);
        }
        // 导航到VIP按钮
        setFocusDelayed('header-join-vip');
        return false; // 阻止默认导航，我们手动处理
      }
      // 向上阻止导航
      if (direction === 'up') {
        return false;
      }
      // 向下允许导航到页面内容元素
      return true;
    },
  });

  // 菜单容器的焦点管理
  const { ref: menuRef } = useFocusable({
    focusKey: menuFocusKey,
    trackChildren: true,
    isFocusBoundary: false, // 允许焦点进入子元素
    // 移除 onBlur 自动关闭逻辑，改为手动控制
  });

  // 处理菜单项准备就绪
  const handleItemReady = useCallback(
    (index: number) => {
      if (index === 0 && isMenuOpen && !showLogoutConfirm) {
        // 第一个菜单项准备就绪时，立即设置焦点
        const targetFocusKey = `${menuFocusKey}-menu-item-0`;

        // 使用多重策略确保焦点设置成功
        setFocusDelayed(targetFocusKey, 0);
        setFocusDelayed(targetFocusKey, 50);
        setFocusDelayed(targetFocusKey, 150);

        // 备用方案：使用原生 DOM focus
        setTimeout(() => {
          if (firstMenuItemRef.current) {
            firstMenuItemRef.current.focus();
          }
        }, 200);

        setMenuItemsReady(true);
      }
    },
    [isMenuOpen, showLogoutConfirm, menuFocusKey, setFocusDelayed]
  );

  // 关闭菜单的回调
  const handleCloseMenu = useCallback(() => {
    if (!showLogoutConfirm) {
      setIsMenuOpen(false);
    }
  }, [showLogoutConfirm]);

  // 处理菜单项点击
  const handleMenuClick = useCallback(
    (menuKey: string) => {
      // 退出登录需要先显示确认对话框
      if (menuKey === 'logout') {
        setShowLogoutConfirm(true);
        return;
      }

      setIsMenuOpen(false);
      onMenuSelect?.(menuKey);
    },
    [onMenuSelect]
  );

  // 处理退出登录确认
  const handleLogoutConfirm = useCallback(() => {
    setShowLogoutConfirm(false);
    setIsMenuOpen(false);

    // 延迟触发实际的退出操作，确保 UI 已更新
    // 注意：焦点恢复由 useUserAuth 处理
    setTimeout(() => {
      onMenuSelect?.('logout');
    }, 50);
  }, [onMenuSelect]);

  // 处理退出登录取消
  const handleLogoutCancel = useCallback(() => {
    setShowLogoutConfirm(false);
    // 由于菜单组件始终存在，可以立即恢复焦点
    setTimeout(() => {
      const logoutItemIndex = menuItems.findIndex((item) => item.key === 'logout');
      if (logoutItemIndex !== -1) {
        setFocus(`${menuFocusKey}-menu-item-${logoutItemIndex}`);
      }
    }, 50);
  }, [menuItems, menuFocusKey]);

  // 处理入口点击（鼠标点击）- 与切焦到个人中心的交互一样
  const handleEntryClick = useCallback(() => {
    // 展开菜单
    setIsMenuOpen(true);
    // 焦点进入菜单第一项
    const targetFocusKey = `${menuFocusKey}-menu-item-0`;
    setFocusWithMultipleRetries(targetFocusKey);
  }, [menuFocusKey, setFocusWithMultipleRetries]);

  // 处理滚轮切换焦点逻辑
  const throttledWheelFocus = useMemo(
    () =>
      throttle((deltaY: number) => {
        const currentFocusKey = getCurrentFocusKey();
        if (!currentFocusKey?.startsWith(`${menuFocusKey}-menu-item-`)) {
          return;
        }

        const currentIndex = parseInt(currentFocusKey.split('-').pop() || '0', 10);
        const totalItems = menuItems.length;

        if (deltaY > 0) {
          // 向下滚动
          const nextIndex = (currentIndex + 1) % totalItems;
          setFocus(`${menuFocusKey}-menu-item-${nextIndex}`);
        } else if (deltaY < 0) {
          // 向上滚动
          const prevIndex = (currentIndex - 1 + totalItems) % totalItems;
          setFocus(`${menuFocusKey}-menu-item-${prevIndex}`);
        }
      }, 150),
    [menuFocusKey, menuItems.length]
  );

  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      if (!isMenuOpen || showLogoutConfirm) return;
      // 阻止冒泡到上层可能存在的滚动容器
      e.stopPropagation();
      throttledWheelFocus(e.deltaY);
    },
    [isMenuOpen, showLogoutConfirm, throttledWheelFocus]
  );

  // 菜单展开时，禁止 body 滚动（防止出现双滚动条或背景抖动）
  useEffect(() => {
    if (isMenuOpen || showLogoutConfirm) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMenuOpen, showLogoutConfirm]);

  // 渲染未登录入口
  const renderLoginEntry = () => (
    <button
      ref={entryRef}
      className={`${styles.profileEntry} ${entryFocused ? styles.profileEntryFocused : ''}`}
      onClick={handleEntryClick}
      // onMouseEnter={() => setFocus(`${focusKey}-entry`)}
    >
      <ProfileIcon className={styles.profileIcon} />
    </button>
  );

  // 渲染已登录入口 - 显示用户头像 + VIP 角标（会员或过期会员显示）
  const renderLoggedInEntry = () => {
    // 判断会员是否过期：曾经是会员（有到期时间）但已过期
    // 将秒级时间戳转换为毫秒级时间戳
    const vipEndTimestampMs = (vipInfo?.endTimestamp ?? 0) * 1000;
    const isVipExpired = (vipInfo?.endTimestamp ?? 0) > 0 && vipEndTimestampMs < Date.now() && !vipInfo?.isVip;
    const shouldShowVipBadge = vipInfo?.isVip || isVipExpired;

    return (
      <button
        ref={entryRef}
        className={`${styles.profileEntry} ${entryFocused ? styles.profileEntryFocused : ''}`}
        onClick={handleEntryClick}
        // onMouseEnter={() => setFocus(`${focusKey}-entry`)}
      >
        <div className={styles.avatarWrapper}>
          {userAvatar ? (
            <img src={userAvatar} alt={userName || 'User'} className={styles.avatarImage} />
          ) : (
            <ProfileIcon className={styles.profileIcon} />
          )}
          {shouldShowVipBadge && <VipBadgeIcon className={styles.vipBadge} {...({ expired: isVipExpired } as any)} />}
        </div>
      </button>
    );
  };

  return (
    <div className={styles.profileMenuContainer} {...props}>
      {/* 全屏遮罩层，用于拦截背景 hover 和点击 */}
      {(isMenuOpen || showLogoutConfirm) && (
        <div
          className={styles.menuMask}
          onClick={handleCloseMenu}
          onWheel={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        />
      )}

      {/* 个人中心入口 */}
      {isLoggedIn ? renderLoggedInEntry() : renderLoginEntry()}

      {/* 下拉菜单 - 始终渲染以确保焦点系统正确注册 */}
      <FocusContext.Provider value={menuFocusKey}>
        <div
          ref={menuRef}
          onWheel={handleWheel}
          className={styles.menuDropdown}
          style={{
            opacity: isMenuOpen || showLogoutConfirm ? 1 : 0,
            pointerEvents: isMenuOpen || showLogoutConfirm ? 'auto' : 'none',
            // 始终保持在 DOM 中，确保焦点系统能够找到子元素
            position: 'absolute', // 确保隐藏时不影响布局
            // 禁用动画，避免首次加载时闪烁
            animation: isMenuOpen || showLogoutConfirm ? undefined : 'none',
            // 使用 visibility 辅助控制，完全隐藏时不可见
            visibility: isMenuOpen || showLogoutConfirm ? 'visible' : 'hidden',
          }}
        >
          {/* 菜单内容始终渲染，避免焦点丢失 */}
          <>
            {/* 已登录时显示用户信息区域（不可获焦） */}
            {isLoggedIn && (
              <UserInfoDisplay
                userAvatar={userAvatar}
                userName={userName}
                isVip={vipInfo?.isVip ?? false}
                vipEndDate={vipInfo?.endDate ?? ''}
                vipEndTimestamp={vipInfo?.endTimestamp ?? 0}
                accountType={accountType}
                tips={vipInfo?.tips}
                t={t}
              />
            )}
            {/* 菜单项 */}
            {menuItems.map((item, index) => (
              <MenuItemComponent
                dt-eid="navigate_bar_user"
                dt-params={item.reportData}
                key={`menu-item-${index}`}
                item={item}
                index={index}
                totalItems={menuItems.length}
                focusKey={menuFocusKey}
                onMenuClick={handleMenuClick}
                onCloseMenu={handleCloseMenu}
                onItemReady={handleItemReady}
                firstItemRef={index === 0 ? firstMenuItemRef : undefined}
                setFocusDelayed={setFocusDelayed}
                t={t}
              />
            ))}
          </>
        </div>
      </FocusContext.Provider>

      {/* 退出登录确认对话框 */}
      <ConfirmDialog
        visible={showLogoutConfirm}
        title={t('profile.confirmLogout') || 'Confirm logout?'}
        confirmText={t('profile.confirm') || 'Confirm'}
        cancelText={t('profile.cancel') || 'Cancel'}
        onConfirm={handleLogoutConfirm}
        onCancel={handleLogoutCancel}
      />
    </div>
  );
};
